import React, { useState } from "react";
import { useMsal, useAccount } from "@azure/msal-react";

interface NavbarProps {
  onNavigate: (page: string) => void;
  activePage: string;
}

const Navbar: React.FC<NavbarProps> = ({ onNavigate, activePage }) => {
  const { accounts, instance } = useMsal();
  const account = useAccount(accounts[0] ?? null);
  const [profileOpen, setProfileOpen] = useState(false);

  const getInitials = (name?: string | null) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  const userInitials = getInitials(account?.name);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#user-profile-dropdown') && !target.closest('#user-initials-btn')) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  const handleLogout = async () => {
    try {
      await instance.logoutRedirect({
        account: account ?? undefined,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const navLinks = [
    { page: "GitHub", label: "Release Management" },
    { page: "ConfigStore", label: "Config Management" },
    { page: "ArgoCD", label: "Kubernetes Deployments" },
  ];

  return (
    <nav className="bg-gradient-to-r from-indigo-600 to-blue-600 shadow-lg px-6 py-4 mb-6">
      <div className="flex items-center justify-between">

        {/* Left — logo + nav links */}
        <div className="flex items-center gap-6">
          <div
            className="text-white cursor-pointer flex items-center"
            onClick={() => onNavigate("Home")}
          >
            <img src="/logo.png" alt="OpsNest Logo" className="h-10 w-auto ml-4" />
            <span className="text-xl font-bold ml-1">OpsNest</span>
          </div>

          {activePage !== "Home" && (
            <div className="flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.page}
                  type="button"
                  onClick={() => onNavigate(link.page)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                    activePage === link.page
                      ? "bg-white bg-opacity-20 text-white"
                      : "text-indigo-100 hover:bg-white hover:bg-opacity-10 hover:text-white"
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right — bell + avatar */}
        <div className="flex items-center space-x-4">
          <div className="relative">
            <button className="p-1 rounded-full text-gray-200 hover:text-white focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </button>
          </div>
          <div className="relative">
            <button
              id="user-initials-btn"
              className="w-10 h-10 rounded-full bg-white border-2 border-white shadow-md flex items-center justify-center overflow-hidden focus:outline-none"
              onClick={() => setProfileOpen((open) => !open)}
              aria-label="User profile"
            >
              <span className="text-sm font-bold text-indigo-700">{userInitials}</span>
            </button>
            {profileOpen && (
              <div id="user-profile-dropdown" className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg z-50 border border-gray-200">
                <div className="flex flex-col items-center p-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
                    <span className="text-lg font-bold text-indigo-700">{userInitials}</span>
                  </div>
                  <div className="text-base font-semibold text-gray-900">{account?.name}</div>
                  <div className="text-sm text-gray-500 mb-2">{account?.username}</div>
                  <button
                    onClick={handleLogout}
                    className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition"
                  >
                    Logout
                  </button>
                </div>
                <div className="absolute top-0 right-8 -mt-2 w-4 h-4 overflow-hidden">
                  <div className="w-4 h-4 bg-white rotate-45 transform border-t border-l border-gray-200 shadow-md"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;